import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, ExternalLink, Trash2, Edit3, CheckCircle2, EyeOff, FileText, MailCheck, Loader2, AlertCircle, Phone, Mail, MailOpen, Inbox, BarChart3 } from "lucide-react";
import { useAuth } from "../lib/auth";
import {
    adminListArticles, adminPublishArticle, adminUnpublishArticle, adminDeleteArticle,
    adminGenerateArticle, adminListContacts, adminMarkContactRead, adminMarkContactUnread,
    adminMarkAllContactsRead, formatApiError,
} from "../lib/api";
import AdminStats from "../components/AdminStats";
import LegalAssessmentPanel from "../components/LegalAssessmentPanel";

const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};
const formatDateTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("articles");
    const [articles, setArticles] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const reload = async () => {
        setLoading(true);
        setError(null);
        try {
            const [arts, cnts] = await Promise.all([adminListArticles(), adminListContacts()]);
            setArticles(arts);
            setContacts(cnts);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    // Al primo caricamento, se ci sono contatti non letti, apri automaticamente la tab Contatti
    useEffect(() => {
        if (!loading && contacts.length > 0) {
            const hasUnread = contacts.some((c) => !c.read);
            if (hasUnread) setTab("contacts");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    const handlePublish = async (id) => {
        try {
            await adminPublishArticle(id);
            setFeedback({ type: "success", text: "Articolo pubblicato" });
            await reload();
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        }
    };
    const handleUnpublish = async (id) => {
        try {
            await adminUnpublishArticle(id);
            setFeedback({ type: "success", text: "Articolo riportato in bozza" });
            await reload();
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        }
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare definitivamente questo articolo?")) return;
        try {
            await adminDeleteArticle(id);
            setFeedback({ type: "success", text: "Articolo eliminato" });
            await reload();
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        }
    };
    const handleGenerate = async () => {
        if (!window.confirm("Generare un nuovo articolo (bozza) dal prossimo argomento in coda? L'operazione richiede ~30 secondi.")) return;
        setGenerating(true);
        setFeedback(null);
        try {
            const res = await adminGenerateArticle();
            setFeedback({ type: "success", text: `Bozza generata: "${res.titolo}"` });
            await reload();
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        } finally {
            setGenerating(false);
        }
    };

    const handleToggleRead = async (contact) => {
        try {
            if (contact.read) {
                await adminMarkContactUnread(contact.id);
            } else {
                await adminMarkContactRead(contact.id);
            }
            await reload();
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const res = await adminMarkAllContactsRead();
            setFeedback({ type: "success", text: `${res.updated} contatti marcati come letti` });
            await reload();
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        }
    };

    const handleAssessmentUpdate = (contactId, assessment) => {
        setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, legal_assessment: assessment } : c)));
    };

    const drafts = articles.filter((a) => a.status === "draft");
    const published = articles.filter((a) => a.status === "published");
    const unreadContacts = contacts.filter((c) => !c.read);
    const unreadCount = unreadContacts.length;

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="bg-navy text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-9 h-9 border border-gold flex items-center justify-center">
                            <span className="font-serif text-gold text-base">MC</span>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] text-gold">Pannello Admin</div>
                            <div className="text-sm">{user?.name || user?.email}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {unreadCount > 0 && (
                            <button
                                onClick={() => setTab("contacts")}
                                data-testid="header-unread-badge"
                                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-gold text-navy text-xs uppercase tracking-[0.18em] font-bold hover:bg-gold-soft transition-colors"
                                title={`${unreadCount} contatti non letti`}
                            >
                                <Inbox size={14} />
                                {unreadCount} non {unreadCount === 1 ? "letto" : "letti"}
                            </button>
                        )}
                        <Link to="/" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold" data-testid="admin-view-site">
                            <ExternalLink size={14} /> Vedi sito
                        </Link>
                        <Link to="/admin/hr" data-testid="admin-nav-hr" className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-gold text-navy text-xs uppercase tracking-[0.18em] font-bold hover:bg-gold-soft transition-colors" title="Dashboard Legal HR">
                            Legal HR
                        </Link>
                        <button
                            onClick={handleLogout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors"
                        >
                            <LogOut size={14} /> Esci
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                    <div>
                        <div className="label-eyebrow mb-3">Gestione Contenuti</div>
                        <h1 className="font-serif text-navy text-4xl md:text-5xl">Dashboard</h1>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleGenerate}
                            disabled={generating}
                            data-testid="admin-generate-article"
                            className="btn-gold"
                        >
                            {generating ? <><Loader2 size={16} className="animate-spin" /> Generazione...</> : <><Plus size={16} /> Nuova bozza AI</>}
                        </button>
                    </div>
                </div>

                {feedback && (
                    <div
                        className={`mb-6 p-4 border flex items-start gap-3 ${feedback.type === "success" ? "border-green-600 bg-green-50 text-green-800" : "border-red-500 bg-red-50 text-red-800"}`}
                        data-testid="admin-feedback"
                    >
                        {feedback.type === "success" ? <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />}
                        <span className="text-sm">{feedback.text}</span>
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 mb-10">
                    <div className="bg-white p-6">
                        <div className="label-eyebrow mb-2">Pubblicati</div>
                        <div className="font-serif text-3xl text-navy">{published.length}</div>
                    </div>
                    <div className="bg-white p-6">
                        <div className="label-eyebrow mb-2">Bozze in attesa</div>
                        <div className="font-serif text-3xl text-navy">{drafts.length}</div>
                    </div>
                    <div className={`bg-white p-6 ${unreadCount > 0 ? "ring-2 ring-gold" : ""}`}>
                        <div className="label-eyebrow mb-2">{unreadCount > 0 ? "Contatti non letti" : "Contatti ricevuti"}</div>
                        <div className="flex items-baseline gap-3">
                            <div className="font-serif text-3xl text-navy">{unreadCount > 0 ? unreadCount : contacts.length}</div>
                            {unreadCount > 0 && (
                                <div className="text-sm text-slate-500">/ {contacts.length} totali</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-300 mb-8">
                    <button
                        onClick={() => setTab("stats")}
                        data-testid="tab-stats"
                        className={`px-6 py-4 text-sm uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${tab === "stats" ? "border-gold text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}
                    >
                        <BarChart3 size={14} /> Statistiche
                    </button>
                    <button
                        onClick={() => setTab("articles")}
                        data-testid="tab-articles"
                        className={`px-6 py-4 text-sm uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px transition-colors ${tab === "articles" ? "border-gold text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}
                    >
                        <FileText size={14} className="inline mr-2" /> Articoli
                    </button>
                    <button
                        onClick={() => setTab("contacts")}
                        data-testid="tab-contacts"
                        className={`px-6 py-4 text-sm uppercase tracking-[0.2em] font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${tab === "contacts" ? "border-gold text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}
                    >
                        <MailCheck size={14} /> Contatti
                        {unreadCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-gold text-navy text-[10px] font-bold rounded-full" data-testid="tab-contacts-badge">
                                {unreadCount}
                            </span>
                        )}
                    </button>
                </div>

                {loading && <div className="py-20 text-center text-slate-500 font-mono text-sm">Caricamento...</div>}
                {error && <div className="py-10 text-red-600 text-sm" data-testid="admin-error">{error}</div>}

                {tab === "stats" && <AdminStats />}

                {!loading && tab === "articles" && (
                    <div className="bg-white border border-slate-200" data-testid="admin-articles-list">
                        {articles.length === 0 ? (
                            <div className="p-12 text-center text-slate-500">Nessun articolo presente.</div>
                        ) : (
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left border-b border-slate-200">
                                        <th className="px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Stato</th>
                                        <th className="px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Titolo</th>
                                        <th className="px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold hidden md:table-cell">Categoria</th>
                                        <th className="px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold hidden lg:table-cell">Creato</th>
                                        <th className="px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold text-right">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articles.map((art) => (
                                        <tr key={art.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-article-row-${art.id}`}>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-2 py-1 text-[9px] uppercase tracking-[0.2em] font-bold ${art.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                                                    {art.status === "published" ? "Pubblicato" : "Bozza"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 max-w-xl">
                                                <div className="font-serif text-navy text-base leading-snug">{art.titolo}</div>
                                                <div className="text-xs text-slate-500 mt-1 line-clamp-1">{art.sommario}</div>
                                            </td>
                                            <td className="px-6 py-4 hidden md:table-cell">
                                                <span className="inline-block px-2 py-1 border border-slate-200 text-[10px] uppercase tracking-[0.2em] text-slate-600">{art.categoria}</span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">{formatDate(art.created_at)}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 justify-end">
                                                    <Link to={`/admin/articoli/${art.id}`} data-testid={`admin-edit-${art.id}`} className="p-2 border border-slate-300 hover:border-gold hover:text-gold-deep transition-colors" title="Modifica">
                                                        <Edit3 size={14} />
                                                    </Link>
                                                    {art.status === "draft" ? (
                                                        <button onClick={() => handlePublish(art.id)} data-testid={`admin-publish-${art.id}`} className="p-2 border border-green-600 text-green-700 hover:bg-green-600 hover:text-white transition-colors" title="Pubblica">
                                                            <CheckCircle2 size={14} />
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => handleUnpublish(art.id)} data-testid={`admin-unpublish-${art.id}`} className="p-2 border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white transition-colors" title="Riporta in bozza">
                                                            <EyeOff size={14} />
                                                        </button>
                                                    )}
                                                    <button onClick={() => handleDelete(art.id)} data-testid={`admin-delete-${art.id}`} className="p-2 border border-red-500 text-red-600 hover:bg-red-500 hover:text-white transition-colors" title="Elimina">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {!loading && tab === "contacts" && (
                    <div data-testid="admin-contacts-list">
                        {contacts.length > 0 && unreadCount > 0 && (
                            <div className="mb-4 flex items-center justify-between">
                                <div className="text-sm text-slate-600">
                                    <strong className="text-navy">{unreadCount}</strong> contatt{unreadCount === 1 ? "o" : "i"} non lett{unreadCount === 1 ? "o" : "i"}
                                </div>
                                <button
                                    onClick={handleMarkAllRead}
                                    data-testid="mark-all-read"
                                    className="text-xs uppercase tracking-[0.2em] text-gold-deep hover:text-navy font-semibold underline-offset-4 hover:underline"
                                >
                                    Segna tutti come letti
                                </button>
                            </div>
                        )}

                        <div className="bg-white border border-slate-200">
                            {contacts.length === 0 ? (
                                <div className="p-12 text-center text-slate-500">Nessun contatto ricevuto.</div>
                            ) : (
                                <div className="divide-y divide-slate-200">
                                    {contacts.map((c) => (
                                        <div
                                            key={c.id}
                                            className={`p-6 transition-colors ${!c.read ? "bg-gold/5 border-l-4 border-gold" : ""}`}
                                            data-testid={`admin-contact-${c.id}`}
                                        >
                                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                                <div className="flex items-center gap-3">
                                                    {!c.read && (
                                                        <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse flex-shrink-0" aria-label="Non letto" />
                                                    )}
                                                    <div className={`font-serif text-lg ${!c.read ? "text-navy font-bold" : "text-slate-700"}`}>
                                                        {c.nome}
                                                    </div>
                                                    {!c.read && (
                                                        <span className="inline-block px-2 py-0.5 bg-gold text-navy text-[9px] uppercase tracking-[0.18em] font-bold">
                                                            Nuovo
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs text-slate-500 font-mono">{formatDateTime(c.created_at)}</span>
                                                    <button
                                                        onClick={() => handleToggleRead(c)}
                                                        data-testid={`toggle-read-${c.id}`}
                                                        title={c.read ? "Segna come non letto" : "Segna come letto"}
                                                        className={`p-1.5 border transition-colors ${c.read ? "border-slate-300 text-slate-500 hover:border-amber-500 hover:text-amber-600" : "border-gold text-gold-deep hover:bg-gold hover:text-navy"}`}
                                                    >
                                                        {c.read ? <Mail size={14} /> : <MailOpen size={14} />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm mb-3">
                                                <a href={`mailto:${c.email}`} className="text-navy hover:text-gold-deep">{c.email}</a>
                                                {c.telefono && (
                                                    <a href={`tel:${c.telefono}`} className="flex items-center gap-1 text-navy hover:text-gold-deep">
                                                        <Phone size={12} /> {c.telefono}
                                                    </a>
                                                )}
                                                {c.richiamo_telefonico && (
                                                    <span className="inline-block px-2 py-1 bg-gold/10 border border-gold text-gold-deep text-[10px] uppercase tracking-[0.2em] font-bold">Richiamo richiesto</span>
                                                )}
                                            </div>
                                            <div className="text-sm text-slate-600 mb-2"><strong className="text-navy">Oggetto:</strong> {c.oggetto}</div>
                                            <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 border-l-2 border-gold">{c.messaggio}</div>
                                            <LegalAssessmentPanel contact={c} onUpdate={handleAssessmentUpdate} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
